import { useEffect } from "react";

import { breakpointMobileOnly } from "src/design-system/styles/breakpoints";
import { useGlobalHeaderConfig } from "src/hooks/useGlobalHeaderConfig";
import { useBreakpointScreenCheck } from "./viewport";
import { useOnce } from "./lifecycle";

function installBodyBackground(color: string): () => void {
  const previous = document.body.style.backgroundColor;
  document.body.style.backgroundColor = color;
  return () => {
    document.body.style.backgroundColor = previous;
  };
}

function installThemeColorMeta(color: string): () => void {
  // update "theme color" to match background for iOS safari.
  let meta: HTMLMetaElement;
  // check first if theme-color meta tag exists, so we don't ever have more than one.
  const existingMeta = document.querySelectorAll("meta[name=theme-color]");
  if (existingMeta.length) {
    meta = existingMeta[0] as HTMLMetaElement;
  } else {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.getElementsByTagName("head")[0].appendChild(meta);
  }

  const oldColor = meta.content;

  meta.content = color;
  return () => {
    meta.content = oldColor;
  };
}

/**
 * `usePageBackground` only while `mobileMediaQuery` matches; defaults to `breakpointMobileOnly`.
 * @param color Body `backgroundColor` and `theme-color` meta.
 * @param mobileMediaQuery `window.matchMedia` query string.
 */
export function usePageBackgroundMobileOnly(
  color: string,
  mobileMediaQuery: string = breakpointMobileOnly
) {
  const globalHeaderConfig = useGlobalHeaderConfig();
  const isNarrowViewport = useBreakpointScreenCheck(mobileMediaQuery);

  useEffect(() => {
    if (!isNarrowViewport) {
      return undefined;
    }

    const restoreThemeMeta = globalHeaderConfig.enabled
      ? undefined
      : installThemeColorMeta(color);
    const restoreBody = installBodyBackground(color);

    return () => {
      restoreBody();
      restoreThemeMeta?.();
    };
  }, [isNarrowViewport, color, globalHeaderConfig.enabled, mobileMediaQuery]);
}

/**
 * Hook used to change the current page background for as long as the component is rendered.
 * @param color The new backgroundColor to be used.
 */
export function usePageBackground(color: string) {
  useMetaThemeColor(color);

  useOnce(() => {
    return installBodyBackground(color);
  });
}

/**
 * Hook used to change the current page background image for as long as the component is rendered.
 * @param image The new background image path to be used.
 */
export function usePageBackgroundImage(image: string | undefined) {
  useOnce(() => {
    const oldImage = document.body.style.backgroundImage;

    document.body.style.backgroundImage = image ? `url('${image}')` : "";

    return () => {
      document.body.style.backgroundImage = oldImage;
    };
  });
}

export function useMetaThemeColor(color: string) {
  const globalHeaderConfig = useGlobalHeaderConfig();

  useOnce(() => {
    if (globalHeaderConfig.enabled) {
      return undefined;
    }
    return installThemeColorMeta(color);
  });
}
